import * as THREE from 'three'
import { TimelineLite, TweenLite } from 'gsap'

import Terrain from '../Terrain.js'
import ScanModel from '../ScanModel.js'
import SimulationInteraction from '../Interactions/Simulation.js'

export default class Training
{
    constructor(_options)
    {
        this.$eventHub = _options.$eventHub
        this.camera = _options.camera
        this.loader = _options.loader
        this.time = _options.time
        this.sizes = _options.sizes
        this.ui = _options.ui
        this.mobile = _options.mobile
        this.cursor = _options.cursor
        this.position = _options.position
        this.terrainGeometry = _options.terrainGeometry

        this.name = 'training'
        this.container = new THREE.Object3D()
        this.container.position.copy(this.position)

        this.setTerrain()
    }

    /**
     * Set terrain
     */
    setTerrain()
    {
        this.terrain = new Terrain({
            mobile: this.mobile,
            time: this.time,
            loader: this.loader,
            ui: this.ui,
            geometry: this.terrainGeometry,
            texture: this.loader.resources.terrainTexture
        })

        this.terrain.container.scale.x = - 1

        this.terrain.plane.material.uniforms.uElevationNoiseMultiplier.value = 0.07
        this.terrain.plane.material.uniforms.uAlpha.value = 0

        this.container.add(this.terrain.container)
    }

    /**
     * Set contents
     */
    setContents()
    {
        this.setModels()
        this.setInteraction()
        this.setSliderTimeline()
        this.setFocusTimeline()
    }

    /**
     * Set models
     */
    setModels()
    {
        /**
         * Room
         */
        this.room = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.simulatorRoomModelObj.geometry,
            revealPosition: { x: -57.66, y: 1.24, z: 18.7 },
            color: { x: 0.12, y: 0.12, z: 0.12 }
        })
        // this.room.container.rotation.y = Math.PI * 0.5
        this.room.container.scale.x = 0.1
        this.room.container.scale.y = 0.1
        this.room.container.scale.z = 0.1
        this.room.container.position.y = 0
        this.room.container.position.z = - 4.85
        this.room.container.position.x = 1
        this.container.add(this.room.container)

        /**
         * Simulator
         */
        this.simulator = {}
        this.simulator.container = new THREE.Object3D()
        this.simulator.container.scale.x = 0.1
        this.simulator.container.scale.y = 0.1
        this.simulator.container.scale.z = 0.1
        this.simulator.container.position.y = 0
        this.simulator.container.position.z = - 4.85
        this.simulator.container.position.x = 1
        // this.simulator.container.rotation.y = - 1.8
        this.container.add(this.simulator.container)

        this.simulator.outside = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.simulatorOutsideModelObj.geometry,
            revealPosition: { x: 5.61, y: 1.24, z: -0.94 },
            color: { x: 0.18, y: 0.18, z: 0.18 }
        })
        this.simulator.container.add(this.simulator.outside.container)

        this.simulator.inside = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.simulatorInsideModelObj.geometry,
            revealPosition: { x: 5.61, y: 5.61, z: 1.24 },
            color: { x: 1, y: 0.9, z: 0 }
        })
        this.simulator.container.add(this.simulator.inside.container)

        // // UI
        // if(this.ui)
        // {
        //     // Folder
        //     const folder = this.ui.addFolder('simulator inside')
        //     folder.open()

        //     // Tweaks
        //     folder
        //         .add(this.simulator.inside.material.uniforms.uRevealPosition.value, 'x', - 100, 100, 0.01)
        //         .name('uRevealPosition x')

        //     folder
        //         .add(this.simulator.inside.material.uniforms.uRevealPosition.value, 'y', - 100, 100, 0.01)
        //         .name('uRevealPosition y')

        //     folder
        //         .add(this.simulator.inside.material.uniforms.uRevealPosition.value, 'z', - 100, 100, 0.01)
        //         .name('uRevealPosition z')

        //     folder
        //         .add(this.simulator.inside.material.uniforms.uRevealDistance, 'value', 0, 100, 0.01)
        //         .name('uRevealDistance')
        // }
    }

    /**
     * Set interaction
     */
    setInteraction()
    {
        this.interaction = new SimulationInteraction({
            $eventHub: this.$eventHub,
            mobile: this.mobile,
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            cursor: this.cursor,
            ui: this.ui,
            loader: this.loader
        })

        this.interaction.container.position.x = 0.9
        this.interaction.container.position.z = - 4.54
        this.interaction.container.position.y = - 13.7

        this.interaction.container.rotation.order = 'YXZ'
        this.interaction.container.rotation.x = 0.2
        this.interaction.container.rotation.y = 0.52
        this.interaction.container.rotation.z = 0

        window.simulation = this.interaction.container
    }

    /**
     * Show
     */
    show()
    {
        this.sliderTimeline.play()
    }

    /**
     * Hide
     */
    hide()
    {
        this.sliderTimeline.reverse()
    }

    /**
     * Set timeline
     */
    setSliderTimeline()
    {
        this.sliderTimeline = new TimelineLite({ paused: true })

        this.sliderTimeline.add(TweenLite.fromTo(this.terrain.plane.material.uniforms.uAlpha, 2, { value: 0 }, { value: 1 }), 1)
        this.sliderTimeline.add(TweenLite.fromTo(this.room.material.uniforms.uRevealDistance, 1.5, { value: 89 }, { value: 40 }), 2.2)
        this.sliderTimeline.add(TweenLite.fromTo(this.simulator.outside.material.uniforms.uRevealDistance, 1, { value: 8.6 }, { value: 2.5 }), 2.9)
        this.sliderTimeline.add(TweenLite.fromTo(this.simulator.inside.material.uniforms.uRevealDistance, 3, { value: 9.72 }, { value: 3.17 }), 2.3)
    }

    /**
     * Set focus timeline
     */
    setFocusTimeline()
    {
        this.focusTimeline = new TimelineLite({ paused: true })

        this.focusTimeline.call(() =>
        {
            // Entering
            if(!this.focusTimeline._reversed)
            {
                this.container.add(this.interaction.container)
            }
            // Leaving
            else
            {
                this.container.remove(this.interaction.container)
            }
        }, null, this, 0)

        // Terrain
        this.focusTimeline.add(TweenLite.fromTo(this.terrain.plane.material.uniforms.uFocusColor.value, 2, { x: 1, y: 0.9, z: 0 }, { x: 0.2, y: 0.2, z: 0.12 }), 0)
        this.focusTimeline.add(TweenLite.fromTo(this.terrain.plane.material.uniforms.uFocusDistance.value, 2, { y: 0.4 }, { y: 0.4 }), 0)
        this.focusTimeline.add(TweenLite.fromTo(this.terrain.plane.material.uniforms.uFocusPosition.value, 2, { x: 0.1, y: 0, z: - 5.2 }, { x: 0.345, y: 0, z: - 5.168 }), 0)
        this.focusTimeline.add(TweenLite.fromTo(this.terrain.plane.material.uniforms.uColor.value, 2, { x: 0.12, y: 0.12, z: 0.12 }, { x: 0.06, y: 0.06, z: 0.06 }), 0)

        // Simulation
        this.focusTimeline.call(() =>
        {
            if(!this.focusTimeline._reversed)
            {
                this.interaction.reset()
            }
        }, null, this, 0)
    }
}



// WEBPACK FOOTER //
// ./src/components/app-experience/Experience/Scenes/Training.js